import './styles.scss';
import React, {Dispatch, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {NavLink} from "react-router-dom";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";

import {IResultsState} from "../../../types/redux/result";

/* Bootstrap */
import {Button,  Col, Container, Row} from "react-bootstrap";

/* Redux */
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {ISearchState} from "../../../types/redux/search";
import {addToSearchHistory, updateSavedBibliographies, setSavedDocuments} from "../../../redux/slices/saved-slice";
import {resetResults, setResults} from "../../../redux/slices/result-slice";
import {IUserState} from "../../../types/redux/user";

/* Components */
import SearchInput from "../search-input";
import DocumentCard from "../../cards/document-card";
import Spinner from "../../util/spinner";
import BibliographyCard from "../../cards/bibliography-card";
import PageToolsTop from "../../toolbars/page-tools-top";

/* Api */
import {useQuery} from "react-query";
import SearchService from '../../../api/search';
import {IPageContext, PageContext} from "../../../types/app";
import SavedService from "../../../api/saved";

/* hooks */

/* Components */
import PageToolsBottom from "../../toolbars/page-tools-bottom";
import SearchAdvanced from "../search-advanced";
import FiltersApplied from "../../filters/filters-applied";
import FiltersSlideOut from "../../filters/filters-slideout";
import {setPrevBibliographySearchState, setPrevDocumentSearchState} from "../../../redux/slices/prev-search-slice";
import {ISavedState} from "../../../types/redux/saved";
import useError from "../../../hooks/useError";
import SearchUtils from "../../../utils/search-utils";

const Search = ():JSX.Element => {

  /* context */
  const pageContext: string = useContext<IPageContext>(PageContext).context;

  /* redux */
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);
  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);
  const userState: IUserState = useSelector<RootState, IUserState>(state => state.user);
  const savedState: ISavedState = useSelector<RootState, ISavedState>(state => state.saved);

  const dispatch:Dispatch<AnyAction> = useDispatch();

  /* local */
  const searchParams: URLSearchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  /* state */
  const [advancedSearchActive, setAdvancedSearchActive]
    = useState<boolean>((searchParams.get(Constants.ADV_KEY) === Constants.TRUE) || (userState.settings.search === Constants.ADVANCED_SEARCH));
  const [noSearchResults, setNoResults] = useState<boolean>(false);

  /* hooks */
  const {handleError} = useError();

  const {data, isSuccess, isError, error} = useQuery<any, Error>(
    [
      searchState.industry,
      searchState.query,
      searchState.applied_filters,
      searchState.dates,
      searchState.db_set,
      searchState.suspendSearch,
      searchState.current_page,
      searchState.sort,
      searchState.results_per_page,
      userState.loggedIn
      ],
    async ({signal}) => {
      const isFacetQuery: boolean = false;
      const noDateQuery: boolean = (searchState.selected_filters_ids.indexOf(Constants.NO_DATE) !== -1);

      dispatch(resetResults())
      if (searchState.suspendSearch) return;

      switch (searchState.db_set) {
        case Constants.DOCUMENTS:
          return await SearchService.solrDocumentQuery(JSON.parse(JSON.stringify(searchState)), signal, isFacetQuery, noDateQuery);

        case Constants.BIBLIOGRAPHY:
          return await SearchService.solrBibliographyQuery(JSON.parse(JSON.stringify(searchState)), signal, isFacetQuery, noDateQuery);
      }
    },
    {
      staleTime: Infinity
    }
  );

  const updateSearchParams = (): void =>{
    if ((searchState.db_set === Constants.BIBLIOGRAPHY) || (searchState.db_set === Constants.DOCUMENTS)) {
      // update url to search params to match search state
      const url:string = '/' + searchState.industry + '/' + searchState.db_set + '/';
      const searchParams: URLSearchParams =  SearchUtils.searchQueryToUrlSearchParams(searchState);
      window.history.replaceState(null, '', url + "?" + searchParams.toString());
    }
  }

  /* Make request to backend to see if any records in results are saved to set the save/un-save button */
  const updateSavedState = (recordIds: string[]):void => {
    switch (searchState.db_set) {
      case Constants.DOCUMENTS:
        /* Documents */
        SavedService.getSavedDocumentsViaIds(recordIds).then((response:any) => {
          dispatch(setSavedDocuments(response.savedDocument));
        }).catch(e => handleError(e));
        break;

      case Constants.BIBLIOGRAPHY:
        /* Bibliographies */
        SavedService.getSavedBibliographiesViaIds(recordIds).then((response:any) => {
          dispatch(updateSavedBibliographies(response.savedBiblio));
        }).catch(e => handleError(e));
        break;
    }
  }

  useEffect(()=> {
    if (isSuccess && data) {
      const count: number = data.response.docs.length;
      if (searchState.db_set === Constants.DOCUMENTS) {
        dispatch(setPrevDocumentSearchState({...searchState}))
      } else {
        dispatch(setPrevBibliographySearchState({...searchState}))
      }

      updateSearchParams();

      const prevSearchUrl: string | null = savedState.history.length > 0 ? savedState.history[0].url : null;
      if ((searchState.db_set === Constants.DOCUMENTS) && (prevSearchUrl !== window.location.href)) {
        dispatch(addToSearchHistory({
          id: -1,
          dateCreated: '',
          searchDateTime: new Date().toISOString(),
          count: data.response.numFound,
          url: window.location.href
        }))
      }

      if (count > 0) {
        dispatch(setResults({data:data, db_set:searchState.db_set}));
        if (userState.loggedIn) {
          const recordIds: string[] = data.response.docs.map((record: any) => record.id);
          updateSavedState(recordIds);
        }
        setNoResults(false);
      } else {
        dispatch(resetResults());
        setNoResults(true);
      }
    } else if (isError) {
      handleError(error);
    }



    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]) // isError, dispatch, setNoResults, data, searchState.query, searchState.applied_filters,  pageContext, error?.message

  useEffect(()=>{
    /* if previous logged out and logged back in, updates saveState for results rendered */
    if (userState.loggedIn) {
      const recordIds: string[] = resultsState.results.map(result => result.id);
      updateSavedState(recordIds);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.loggedIn])

  useEffect(()=>{
    if (searchState.db_set !== Constants.DOCUMENTS) {
      setAdvancedSearchActive(false)
    }
  }, [searchState.db_set, setAdvancedSearchActive])

  const handleNoDocuments = (): JSX.Element => {
    let elem: JSX.Element;
    if (noSearchResults) {
      /* done loading with no results */
      if (searchState.applied_filters.filter(f=>f.id !== Constants.FOLDERS).length > 0) {
        elem = <div className={'mt-4 text-center'}>
          <p className={'p-2'} >No results found based on selected filters.</p>
        </div>
      } else {
        elem = <div className={'mt-4 text-center'}>
          <p className={'p-2'} >No results found.</p>
        </div>
      }
    } else if (isError) {
      /* Error messaging  */
      elem = <div className={'mt-4 text-center'}/>
    } else {
      /* loading */
      elem = <div className={'mt-4 text-center'}>
        <Spinner size={Constants.MEDIUM}/>
      </div>
    }
    return elem;
  }

  const getCard = (dbSet: string | null, record: any, index: number): JSX.Element => {
    let card: JSX.Element = <></>;
    switch (dbSet) {
      case Constants.DOCUMENTS:
        card = <DocumentCard
          key={`dc-${index}`}
          index={index}
          record={record}
        />
        break;

      case Constants.BIBLIOGRAPHY:
        card = <BibliographyCard
          key={`dc-${index}`}
          index={index}
          record={record}
        />
        break;

      default:
        // fail case : null
        break;
    }
    return card
  }

  /* Learn More Link */
  const learnMore = useCallback(():JSX.Element =>{
    return (
      <>
        {(pageContext === Constants.INDUSTRY) &&
          <div className={'info-btn search-info-link'}>
            <NavLink
              to={'/resources/search-tips/'}
              className={'text-smaller link text-nowrap'}
            >
              Search Tips
            </NavLink>
          </div>}
      </>
    )
  }, [pageContext]);

  /* Advanced Search Button */
  const advanceSearchBtn = useCallback(():JSX.Element =>{
    return ( <>
      {(searchState.db_set === Constants.DOCUMENTS) &&
        <Button
          variant={'light'}
          id={'search-toggle-btn'}
          className={'bg-transparent btn-link text-smaller text-nowrap'}
          onClick = {()=>setAdvancedSearchActive(!advancedSearchActive)}
        >
          {advancedSearchActive ? 'Simple Search' : 'Advanced Search'}
        </Button>}
    </>)
  }, [searchState.db_set, advancedSearchActive])

  const filtersSlideOut = useCallback(():JSX.Element =>{
    //if (!searchState.initialSearch) return <></>
    return <FiltersSlideOut label={'Filters'}/>
  }, []);

  return (
    <div className={'search'}>
      <Container fluid={true} className={"px-0 px-lg-4 py-0 py-lg-4 mt-0 mt-lg-4 pb-5 bg-white rounded-md border"}>

        {/* mobile */}
        <Container className={"d-lg-none p-0 bg-white"}>
          <Row className={'search-container p-0 m-0  pt-3'}>
            {!advancedSearchActive && <SearchInput
              minimal={false}
            />}

            <Row className={'m-0 mt-2 mb-2'}>
              <Col className={'mb-1 p-0'}>
                {/*<FiltersSlideOut label={'Filters'}/>*/}
                {filtersSlideOut()}
              </Col>

              <div className="vertical-hr"></div>

              <Col className={'text-end'}>
                {advanceSearchBtn()}
              </Col>
            </Row>

            {advancedSearchActive &&
              <Row className={'p-0 m-0'}>
                <SearchAdvanced/>
              </Row>
            }
          </Row>

          <Row className={'p-0 m-0'}>
            <FiltersApplied/>
          </Row>

          {/* Page tools */}
          <PageToolsTop/>

        </Container>

        {/* Desktop */}
        <Container className={"d-none d-lg-block p-0 m-0"}>

          {/* Search Form Input */}
          {<Row className={'input-form flex-nowrap align-items-top mb-4'}>
            <Col lg={9} className={'d-flex justify-content-start ' + searchState.db_set}>
              <FiltersSlideOut label={'Filters'}/>

              {!advancedSearchActive && <SearchInput
                minimal={false}
              />}

              {advancedSearchActive &&
                <SearchAdvanced/>}
            </Col>

            <Col lg={3} className={'d-flex p-0 pt-2 pe-2 justify-content-end ' + searchState.db_set}>
              {(searchState.db_set === Constants.DOCUMENTS) &&
                <>
                  <div>
                    {advanceSearchBtn()}
                  </div>

                  <div className={'vertical-hr btn-hr'}></div>
                </>}

              {/* Learn More Link */}
              <div className={'px-2 '}>{learnMore()}</div>
            </Col>
          </Row>}

          {/* Page tools */}
          <PageToolsTop/>

        </Container>

        {/* Results */}
        <Row className={'mx-0 mt-4'}>
          {/* if no results */}
          {(resultsState.results.length === 0) &&
            handleNoDocuments()
          }

          {/* if results */}
          {resultsState.results.map((result, index) => {
            return getCard(searchState.db_set, result, index)
          })}
        </Row>

        {/* Page tools bottom */}
        {(resultsState.results.length > 0) &&
          <PageToolsBottom/>
        }
      </Container>
    </div>
  )
}
export default Search;
